import { drawing } from '@progress/kendo-drawing';
import { defined, isDefined, isFunction, remove, isString } from '../utils/functions.js';
import '../math/Graph.js';
import { Point, Rect } from '../math/Shapes.js';
import { PathDefiner } from '../math/PathDefiner.js';
import { deepExtend } from '../utils/deep-extend.js';
import { filterConnectionDataItem, isAutoConnector, closestConnector } from './utils.js';
import { CascadingRouter } from '../services/routing/CascadingRouter.js';
import { TRANSPARENT, AUTO, CONNECTION_CONTENT_OFFSET, CASCADING, NONE, MAXINT } from '../constants.js';
import { Connector } from './Connector.js';
import { PolylineRouter } from '../services/routing/PolylineRouter.js';
import { ConnectionEditAdorner } from '../services/adorners/ConnectionEditAdorner.js';
import '../svg/Rotation.js';
import { Polyline } from '../svg/Polyline.js';
import { TextBlock } from '../svg/TextBlock.js';
import { ConnectionEditUnit } from '../services/ConnectionEditUnit.js';
import { Shape } from './Shape.js';
import { DiagramElement } from './DiagramElement.js';

const defaultOptions = {
    hover: {
        stroke: {}
    },
    startCap: NONE,
    endCap: NONE,
    points: [],
    selectable: true,
    fromConnector: AUTO,
    toConnector: AUTO
};
/**
 * The visual link between two Shapes through the intermediate of Connectors.
 */
class Connection extends DiagramElement {
    constructor(from, to, options) {
        options = deepExtend({}, defaultOptions, options);
        super(options);
        this.updateOptionsFromModel();
        this._initRouter();
        this.path = new Polyline(this.options);
        this.path.fill(TRANSPARENT);
        this.visual.append(this.path);
        this._sourcePoint = this._targetPoint = new Point();
        this._setSource(from);
        this._setTarget(to);
        this.content(this.options.content);
        this.definers = [];
        if (defined(options) && options.points) {
            this.points(options.points);
        }
    }
    _setOptionsFromModel(model) {
        this.updateOptionsFromModel(model || this.dataItem);
    }
    updateOptionsFromModel(model) {
        if (this.diagram && this.diagram._isEditable) {
            const dataMap = this.diagram._dataMap;
            const options = filterConnectionDataItem(model || this.dataItem);
            if (model) {
                if (defined(options.from)) {
                    let from = dataMap[options.from];
                    if (from && defined(options.fromConnector)) {
                        from = from.getConnector(options.fromConnector);
                    }
                    this.source(from);
                }
                else if (defined(options.fromX) && defined(options.fromY)) {
                    this.source(new Point(options.fromX, options.fromY));
                }
                if (defined(options.to)) {
                    let to = dataMap[options.to];
                    if (to && defined(options.toConnector)) {
                        to = to.getConnector(options.toConnector);
                    }
                    this.target(to);
                }
                else if (defined(options.toX) && defined(options.toY)) {
                    this.target(new Point(options.toX, options.toY));
                }
                if (defined(options.type) && this.type() !== options.type) {
                    this.points([]);
                    this.type(options.type);
                }
                this.dataItem = model;
                this._template();
                this.redraw(this.options);
            }
            else {
                this.options = deepExtend({}, options, this.options);
            }
        }
    }
    updateModel(syncChanges) {
        if (this.diagram && this.diagram._isEditable) {
            this.diagram.updateConnectionModel(this, syncChanges);
        }
    }
    /**
     * Gets the Point where the source of the connection resides.
     * If the endpoint in Auto-connector the location of the resolved connector will be returned.
     * If the endpoint is floating the location of the endpoint is returned.
     */
    sourcePoint() {
        return this._resolvedSourceConnector ? this._resolvedSourceConnector.position() : this._sourcePoint;
    }
    _setSource(source) {
        const shapeSource = source instanceof Shape;
        const defaultConnector = this.options.fromConnector || AUTO;
        let dataItem;
        if (shapeSource && !source.getConnector(defaultConnector)) {
            return;
        }
        if (source !== undefined) {
            this.from = source;
        }
        this._removeFromSourceConnector();
        if (source === null) { // detach
            if (this.sourceConnector) {
                this._sourcePoint = (this._resolvedSourceConnector || this.sourceConnector).position();
                this._clearSourceConnector();
                this._setFromOptions(null, this._sourcePoint);
            }
        }
        else if (source instanceof Connector) {
            dataItem = source.shape.dataItem;
            if (dataItem) {
                this._setFromOptions(dataItem.id);
            }
            this.sourceConnector = source;
            this.sourceConnector.connections.push(this);
        }
        else if (source instanceof Point) {
            this._setFromOptions(null, source);
            this._sourcePoint = source;
            if (this.sourceConnector) {
                this._clearSourceConnector();
            }
        }
        else if (shapeSource) {
            dataItem = source.dataItem;
            if (dataItem) {
                this._setFromOptions(dataItem.id);
            }
            this.sourceConnector = source.getConnector(defaultConnector);
            this.sourceConnector.connections.push(this);
        }
    }
    source(source, undoable) {
        if (isDefined(source)) {
            if (undoable && this.diagram) {
                this.diagram.undoRedoService.addCompositeItem(new ConnectionEditUnit(this, source));
            }
            this._setSource(source);
            this.refresh();
        }
        return this.sourceConnector ? this.sourceConnector : this._sourcePoint;
    }
    _setFromOptions(from, fromPoint) {
        this.options.from = from;
        if (fromPoint) {
            this.options.fromX = fromPoint.x;
            this.options.fromY = fromPoint.y;
        }
        else {
            this.options.fromX = null;
            this.options.fromY = null;
        }
    }
    /**
     * Gets or sets the PathDefiner of the sourcePoint.
     * The left part of this definer is always null since it defines the source tangent.
     *
     * @param value
     * @returns {*}
     */
    sourceDefiner(value) {
        if (value) {
            if (value instanceof PathDefiner) {
                value.left = null;
                this._sourceDefiner = value;
                this.source(value.point); // refresh implicit here
            }
            else {
                throw new Error('The sourceDefiner needs to be a PathDefiner.');
            }
        }
        else {
            if (!this._sourceDefiner) {
                this._sourceDefiner = new PathDefiner(this.sourcePoint(), null, null);
            }
            return this._sourceDefiner;
        }
    }
    /**
     * Gets  the Point where the target of the connection resides.
     */
    targetPoint() {
        return this._resolvedTargetConnector ? this._resolvedTargetConnector.position() : this._targetPoint;
    }
    _setTarget(target) {
        const shapeTarget = target instanceof Shape;
        const defaultConnector = this.options.toConnector || AUTO;
        let dataItem;
        if (shapeTarget && !target.getConnector(defaultConnector)) {
            return;
        }
        if (target !== undefined) {
            this.to = target;
        }
        this._removeFromTargetConnector();
        if (target === null) { // detach
            if (this.targetConnector) {
                this._targetPoint = (this._resolvedTargetConnector || this.targetConnector).position();
                this._clearTargetConnector();
                this._setToOptions(null, this._targetPoint);
            }
        }
        else if (target instanceof Connector) {
            dataItem = target.shape.dataItem;
            if (dataItem) {
                this._setToOptions(dataItem.id);
            }
            this.targetConnector = target;
            this.targetConnector.connections.push(this);
        }
        else if (target instanceof Point) {
            this._setToOptions(null, target);
            this._targetPoint = target;
            if (this.targetConnector) {
                this._clearTargetConnector();
            }
        }
        else if (shapeTarget) {
            dataItem = target.dataItem;
            if (dataItem) {
                this._setToOptions(dataItem.id);
            }
            this.targetConnector = target.getConnector(defaultConnector);
            this.targetConnector.connections.push(this);
        }
    }
    target(target, undoable) {
        if (isDefined(target)) {
            if (undoable && this.diagram) {
                this.diagram.undoRedoService.addCompositeItem(new ConnectionEditUnit(this, undefined, target));
            }
            this._setTarget(target);
            this.refresh();
        }
        return this.targetConnector ? this.targetConnector : this._targetPoint;
    }
    _setToOptions(to, toPoint) {
        this.options.to = to;
        if (toPoint) {
            this.options.toX = toPoint.x;
            this.options.toY = toPoint.y;
        }
        else {
            this.options.toX = null;
            this.options.toY = null;
        }
    }
    /**
     * Gets or sets the PathDefiner of the targetPoint.
     * The right part of this definer is always null since it defines the target tangent.
     *
     * @param value
     * @returns {*}
     */
    targetDefiner(value) {
        if (value) {
            if (value instanceof PathDefiner) {
                value.right = null;
                this._targetDefiner = value;
                this.target(value.point); // refresh implicit here
            }
            else {
                throw new Error('The sourceDefiner needs to be a PathDefiner.');
            }
        }
        else {
            if (!this._targetDefiner) {
                this._targetDefiner = new PathDefiner(this.targetPoint(), null, null);
            }
            return this._targetDefiner;
        }
    }
    _updateConnectors() {
        this._updateConnector(this.source(), 'source');
        this._updateConnector(this.target(), 'target');
    }
    _updateConnector(instance, name) {
        const diagram = this.diagram;
        if (instance instanceof Connector && !diagram.getShapeById(instance.shape.id)) {
            const dataItem = instance.shape.dataItem;
            const connectorName = instance.options.name;
            const setNewTarget = () => {
                const shape = diagram._dataMap[dataItem.id];
                instance = shape.getConnector(connectorName);
                this[name](instance, false);
                this.updateModel();
            };
            if (diagram._dataMap[dataItem.id]) {
                setNewTarget();
            }
            else {
                const inactiveItem = diagram._inactiveShapeItems.getByUid(dataItem.uid);
                if (inactiveItem) {
                    diagram._deferredConnectionUpdates.push(inactiveItem.onActivate(setNewTarget));
                }
            }
        }
        else {
            this[name](instance, false);
        }
    }
    content(content) {
        const result = this._content(content);
        if (defined(content)) {
            this._alignContent();
        }
        return result;
    }
    _createContentVisual(options) {
        let visual;
        if (isFunction(options.visual)) {
            visual = options.visual.call(this, options);
        }
        else if (options.text) {
            visual = new TextBlock(options);
        }
        if (visual) {
            this._contentVisual = visual;
            visual._includeInBBox = false;
            this.visual.append(visual);
        }
        return visual;
    }
    _updateContentVisual(options) {
        if (isFunction(options.visual)) {
            this.visual.remove(this._contentVisual);
            this._createContentVisual(options);
        }
        else {
            this._contentVisual.redraw(options);
        }
    }
    _alignContent() {
        if (this._contentVisual) {
            let offset = CONNECTION_CONTENT_OFFSET;
            const points = this.allPoints();
            let endIdx = Math.floor(points.length / 2);
            let startIdx = endIdx - 1;
            while (startIdx > 0 && points[startIdx].equals(points[endIdx])) {
                startIdx--;
                endIdx++;
            }
            let endPoint = points[endIdx];
            let startPoint = points[startIdx];
            const boundingBox = this._contentVisual._measure();
            const width = boundingBox.width;
            const height = boundingBox.height;
            let alignToPath = points.length % 2 === 0;
            const distance = startPoint.distanceTo(endPoint);
            if (alignToPath && points.length > 2 && distance > 0 &&
                ((startPoint.y === endPoint.y && distance < width) || (startPoint.x === endPoint.x && distance < height))) {
                alignToPath = false;
                offset = 0;
            }
            let point;
            if (alignToPath) {
                const angle = drawing.util.deg(Math.atan2(endPoint.y - startPoint.y, endPoint.x - startPoint.x));
                point = new Point((endPoint.x - startPoint.x) / 2 + startPoint.x, (endPoint.y - startPoint.y) / 2 + startPoint.y);
                if (Math.abs(angle) === 90) {
                    point.x += offset;
                    point.y -= height / 2;
                }
                else if (angle % 180 === 0) {
                    point.x -= width / 2;
                    point.y -= height + offset;
                }
                else if (angle < -90 || (0 < angle && angle < 90)) {
                    point.y -= height;
                }
                else if (angle < 0 || angle > 90) {
                    point.x -= width;
                    point.y -= height;
                }
            }
            else {
                const midIdx = Math.floor(points.length / 2);
                point = points[midIdx].clone();
                startPoint = points[midIdx - 1];
                endPoint = points[midIdx + 1];
                const offsetX = startPoint.x <= point.x && endPoint.x <= point.x ? offset : -boundingBox.width - offset;
                const offsetY = startPoint.y <= point.y && endPoint.y <= point.y ? offset : -boundingBox.height - offset;
                point.x += offsetX;
                point.y += offsetY;
            }
            this._contentVisual.position(point);
        }
    }
    /**
     * Selects or unselects this connections.
     *
     * @param value True to select, false to unselect.
     */
    select(value) {
        const diagram = this.diagram;
        let selected, deselected;
        if (this._canSelect()) {
            if (this.isSelected !== value) {
                this.isSelected = value;
                selected = [];
                deselected = [];
                if (this.isSelected) {
                    this.adorner = new ConnectionEditAdorner(this, this.options.selection);
                    diagram._adorn(this.adorner, true);
                    diagram._selectedItems.push(this);
                    selected.push(this);
                }
                else {
                    if (this.adorner) {
                        diagram._adorn(this.adorner, false);
                        remove(diagram._selectedItems, this);
                        this.adorner = undefined;
                        deselected.push(this);
                    }
                }
                if (this.adorner) {
                    this.adorner.refresh();
                }
                if (!diagram._internalSelection) {
                    diagram._selectionChanged(selected, deselected);
                }
                return true;
            }
        }
    }
    /**
     * Gets or sets the bounds of this connection.
     *
     * @param value A Rect object.
     * @remark This is automatically set in the refresh().
     * @returns {Rect}
     */
    bounds(value) {
        if (value && !isString(value)) {
            this._bounds = value;
        }
        else {
            return this._bounds;
        }
    }
    /**
     * Gets or sets the connection type (see ConnectionType enumeration).
     *
     * @param value A ConnectionType value.
     * @returns {ConnectionType}
     */
    type(value) {
        const options = this.options;
        if (value) {
            if (value !== options.type) {
                options.type = value;
                this._initRouter();
                this.refresh();
            }
        }
        else {
            return options.type;
        }
    }
    _initRouter() {
        const type = (this.options.type || '').toLowerCase();
        if (type === CASCADING) {
            this._router = new CascadingRouter(this);
        }
        else {
            this._router = new PolylineRouter(this);
        }
    }
    /**
     * Gets or sets the collection of *intermediate* points.
     * The 'allPoints()' property will return all the points.
     * The 'definers' property returns the definers of the intermediate points.
     * The 'sourceDefiner' and 'targetDefiner' return the definers of the endpoints.
     *
     * @param value
     */
    points(value) {
        if (value) {
            this.definers = [];
            for (let i = 0; i < value.length; i++) {
                const definition = value[i];
                if (definition instanceof Point) {
                    this.definers.push(new PathDefiner(definition));
                }
                else if (Object.prototype.hasOwnProperty.call(definition, 'x') && Object.prototype.hasOwnProperty.call(definition, 'y')) { // e.g. Clipboard does not preserve the Point definition and turned into an Object
                    this.definers.push(new PathDefiner(new Point(definition.x, definition.y)));
                }
                else {
                    throw new Error('A Connection point needs to be a Point or an object with x and y properties.');
                }
            }
        }
        else {
            const pts = [];
            if (isDefined(this.definers)) {
                for (let k = 0; k < this.definers.length; k++) {
                    pts.push(this.definers[k].point);
                }
            }
            return pts;
        }
    }
    /**
     * Gets all the points of this connection. This is the combination of the sourcePoint, the points and the targetPoint.
     *
     * @returns {Array}
     */
    allPoints() {
        const pts = [this.sourcePoint()];
        if (this.definers) {
            for (let k = 0; k < this.definers.length; k++) {
                pts.push(this.definers[k].point);
            }
        }
        pts.push(this.targetPoint());
        return pts;
    }
    refresh() {
        this._resolveConnectors();
        this._refreshPath();
        this._alignContent();
        if (this.adorner) {
            this.adorner.refresh();
        }
    }
    _resolveConnectors() {
        let sourcePoint, targetPoint, sourceConnectors, targetConnectors;
        const source = this.source(), target = this.target();
        if (source instanceof Point) {
            sourcePoint = source;
        }
        else if (source instanceof Connector) {
            if (isAutoConnector(source)) {
                sourceConnectors = source.shape.connectors;
            }
            else {
                sourceConnectors = [source];
            }
        }
        if (target instanceof Point) {
            targetPoint = target;
        }
        else if (target instanceof Connector) {
            if (isAutoConnector(target)) {
                targetConnectors = target.shape.connectors;
            }
            else {
                targetConnectors = [target];
            }
        }
        if (sourcePoint) {
            if (targetConnectors) {
                this._resolvedTargetConnector = closestConnector(sourcePoint, targetConnectors);
            }
        }
        else if (sourceConnectors) {
            if (targetPoint) {
                this._resolvedSourceConnector = closestConnector(targetPoint, sourceConnectors);
            }
            else if (targetConnectors) {
                this._resolveAutoConnectors(sourceConnectors, targetConnectors);
            }
        }
    }
    _resolveAutoConnectors(sourceConnectors, targetConnectors) {
        let minNonConflict = MAXINT;
        let minDist = MAXINT;
        let minNonConflictSource, minNonConflictTarget;
        let sourcePoint, targetPoint;
        let minSource, minTarget;
        let sourceConnector, targetConnector;
        let sourceIdx, targetIdx;
        let dist;
        for (sourceIdx = 0; sourceIdx < sourceConnectors.length; sourceIdx++) {
            sourceConnector = sourceConnectors[sourceIdx];
            if (!isAutoConnector(sourceConnector)) {
                sourcePoint = sourceConnector.position();
                for (targetIdx = 0; targetIdx < targetConnectors.length; targetIdx++) {
                    targetConnector = targetConnectors[targetIdx];
                    if (!isAutoConnector(targetConnector)) {
                        targetPoint = targetConnector.position();
                        dist = Math.round(sourcePoint.distanceTo(targetPoint));
                        if (dist < minNonConflict && this.diagram && this._testRoutePoints(sourcePoint, targetPoint, sourceConnector, targetConnector)) {
                            minNonConflict = dist;
                            minNonConflictSource = sourceConnector;
                            minNonConflictTarget = targetConnector;
                        }
                        if (dist < minDist) {
                            minSource = sourceConnector;
                            minTarget = targetConnector;
                            minDist = dist;
                        }
                    }
                }
            }
        }
        if (minNonConflictSource) {
            minSource = minNonConflictSource;
            minTarget = minNonConflictTarget;
        }
        this._resolvedSourceConnector = minSource;
        this._resolvedTargetConnector = minTarget;
    }
    _testRoutePoints(sourcePoint, targetPoint, sourceConnector, targetConnector) {
        const router = this._router;
        let passRoute = true;
        if (router instanceof CascadingRouter) {
            const points = router.routePoints(sourcePoint, targetPoint, sourceConnector, targetConnector), exclude = this._getRouteExclude(sourcePoint, targetPoint, sourceConnector.shape, targetConnector.shape);
            let start, end, rect;
            points.unshift(sourcePoint);
            points.push(targetPoint);
            for (let idx = 1; idx < points.length; idx++) {
                start = points[idx - 1];
                end = points[idx];
                rect = new Rect(Math.min(start.x, end.x), Math.min(start.y, end.y), Math.abs(start.x - end.x), Math.abs(start.y - end.y));
                if (rect.width > 0) {
                    rect.x++;
                    rect.width -= 2;
                }
                if (rect.height > 0) {
                    rect.y++;
                    rect.height -= 2;
                }
                if (!rect.isEmpty() && this.diagram._shapesQuadTree.hitTestRect(rect, exclude)) {
                    passRoute = false;
                    break;
                }
            }
        }
        return passRoute;
    }
    _getRouteExclude(sourcePoint, targetPoint, sourceShape, targetShape) {
        const exclude = [];
        if (this._isPointInsideShape(sourcePoint, sourceShape)) {
            exclude.push(sourceShape);
        }
        if (this._isPointInsideShape(targetPoint, targetShape)) {
            exclude.push(targetShape);
        }
        return exclude;
    }
    _isPointInsideShape(point, shape) {
        const bounds = shape.bounds(), angle = shape.rotate().angle, boundsX = bounds.x, boundsY = bounds.y;
        const rotatedPoint = point.clone().rotate(angle, bounds.center());
        const pointX = rotatedPoint.x;
        const pointY = rotatedPoint.y;
        return pointX > boundsX && pointX < (boundsX + bounds.width) && pointY > boundsY && pointY < (boundsY + bounds.height);
    }
    redraw(options) {
        if (options) {
            this.options = deepExtend({}, this.options, options);
            const points = this.options.points;
            if (defined(points) && points.length > 0) {
                this.points(points);
                this._refreshPath();
            }
            if ((options && options.content) || options.text) {
                this.content(options.content);
            }
            this.path.redraw({
                fill: options.fill,
                stroke: options.stroke,
                startCap: options.startCap,
                endCap: options.endCap
            });
        }
    }
    /**
     * Returns a clone of this connection.
     *
     * @returns {Connection}
     */
    clone() {
        const json = this.serialize();
        if (this.diagram && this.diagram._isEditable && defined(this.dataItem)) {
            json.options.dataItem = this.diagram.options.cloneDataItem(this.dataItem);
        }
        return new Connection(this.from, this.to, json.options);
    }
    /**
     * Returns a serialized connection in json format. Consist of the options and the dataItem.
     *
     * @returns {Connection}
     */
    serialize() {
        const from = this.from.toJSON ? this.from.toJSON : this.from.toString(), to = this.to.toJSON ? this.to.toJSON : this.to.toString();
        const json = deepExtend({}, {
            options: this.options,
            from: from,
            to: to
        });
        if (defined(this.dataItem)) {
            json.dataItem = this.dataItem.toString();
        }
        json.options.points = this.points();
        return json;
    }
    /**
     * Returns whether the given Point or Rect hits this connection.
     *
     * @param value
     * @returns {Connection}
     * @private
     */
    _hitTest(value) {
        if (this.visible()) {
            const p = new Point(value.x, value.y), from = this.sourcePoint(), to = this.targetPoint();
            if (value.isEmpty && !value.isEmpty() && value.contains(from) && value.contains(to)) {
                return this;
            }
            if (this._router.hitTest(p)) {
                return this;
            }
        }
    }
    _hover(value) {
        let color = (this.options.stroke || {}).color;
        if (value && isDefined(this.options.hover.stroke.color)) {
            color = this.options.hover.stroke.color;
        }
        this.path.redraw({
            stroke: {
                color: color
            }
        });
    }
    _refreshPath() {
        if (!defined(this.path)) {
            return;
        }
        this._drawPath();
        this.bounds(this._router.getBounds());
    }
    _drawPath() {
        if (this._router) {
            this._router.route(); // sets the intermediate points
        }
        const source = this.sourcePoint();
        const target = this.targetPoint();
        const points = this.points();
        this.path.redraw({
            points: [source].concat(points, [target])
        });
    }
    _clearSourceConnector() {
        this.sourceConnector = undefined;
        this._resolvedSourceConnector = undefined;
    }
    _clearTargetConnector() {
        this.targetConnector = undefined;
        this._resolvedTargetConnector = undefined;
    }
    _removeFromSourceConnector() {
        if (this.sourceConnector) {
            remove(this.sourceConnector.connections, this);
        }
    }
    _removeFromTargetConnector() {
        if (this.targetConnector) {
            remove(this.targetConnector.connections, this);
        }
    }
    toJSON() {
        let from, to, point;
        if (this.from && this.from.toJSON) {
            from = this.from.toJSON();
        }
        else {
            point = this._sourcePoint;
            from = {
                x: point.x,
                y: point.y
            };
        }
        if (this.to && this.to.toJSON) {
            to = this.to.toJSON();
        }
        else {
            point = this._targetPoint;
            to = {
                x: point.x,
                y: point.y
            };
        }
        return {
            from: from,
            to: to
        };
    }
}

export { Connection };
